const settings = {
    selectors: {
        // Form inputs
        form: ':scope form',

        specialistInput: '#search-specialist',
        queryInput: '#search-query',

        reconstructionTypeIbrCheckbox: '#search-type-ibr',
        reconstructionTypeAfbrCheckbox: '#search-type-afbr',
        reconstructionTypeAfcCheckbox: '#search-type-afc',
        distanceInput: '#search-distance',

        submitButton: 'button[type=\'submit\']',
        clearFiltersButton: '#search-clear-filters',
    },

    queryParameterNames: {
        specialistType: 'type',
        query: 'query',
        reconstructionTypeIbr: 'rt1',
        reconstructionTypeAfbr: 'rt2',
        reconstructionTypeAfc: 'rt3',
        distance: 'distance',
    },

    classNames: {
        // ...
    },
};

export default class Form {
    constructor(container, callbacks = {}) {
        this.container = container;

        this.submitEnabled = false;
        this.clearFiltersEnabled = false;

        this.lastValidatedQueryParameters = null;

        // Get references to our DOM elements
        this.dom = {};
        for (const key in settings.selectors) {
            if (Object.hasOwn(settings.selectors, key)) {
                switch (key) {
                    case 'container':
                        continue;

                    default:
                        const element = container.querySelector(
                            settings.selectors[key],
                        );
                        if (!element) {
                            // console.log(settings.selectors[key]);
                            throw new Error();
                        }

                        this.dom[key] = element;
                        break;
                }
            }
        }

        // Check for query values and prefill form values based on query
        prefillFormFromUrl.call(this);

        // Setup autocomplete on query field
        new google.maps.places.Autocomplete(this.dom.queryInput, {
            componentRestrictions: {country: 'us'},
            fields: ['address_components', 'geometry'],
        });

        // Setup clear filters button
        this.dom.clearFiltersButton.addEventListener('click', (event) => {
            event.preventDefault();

            // Reset filter fields
            this.dom.reconstructionTypeIbrCheckbox.checked = true;
            this.dom.reconstructionTypeAfbrCheckbox.checked = true;
            this.dom.reconstructionTypeAfcCheckbox.checked = true;

            this.dom.distanceInput.selectedIndex = 0;

            // Re-validate & update search
            if (this.validate()) {
                if (callbacks.canAutoSearchOnInputChanges) {
                    if (callbacks.canAutoSearchOnInputChanges() === true) {
                        if (callbacks.searchCallback) {
                            callbacks.searchCallback();
                        }
                    }
                }
            }
        });

        // Setup form submit
        this.dom.form.addEventListener('submit', (event) => {
            event.preventDefault();

            // Re-validate and perform search if successful
            if (this.validate()) {
                if (callbacks.searchCallback) {
                    callbacks.searchCallback();
                }
            }
        });

        // Monitor the state of the query input
        this.dom.queryInput.addEventListener('input', (event) => {
            // Re-validate
            this.validate(false);
        });

        this.dom.queryInput.addEventListener('change', (event) => {
            // Re-validate
            this.validate();
        });

        // Remaining inputs trigger a re-search when changed
        const inputChangeCallback = () => {
            // Re-validate and perform search if successful
            if (this.validate()) {
                if (callbacks.canAutoSearchOnInputChanges) {
                    if (callbacks.canAutoSearchOnInputChanges() === true) {
                        if (callbacks.searchCallback) {
                            callbacks.searchCallback();
                        }
                    }
                }
            }
        };

        // Monitor the state of the specialist type input
        this.dom.specialistInput.addEventListener('change', inputChangeCallback);

        // Monitor the state of the reconstruction type inputs
        this.dom.reconstructionTypeIbrCheckbox.addEventListener('change', inputChangeCallback);
        this.dom.reconstructionTypeAfbrCheckbox.addEventListener('change', inputChangeCallback);
        this.dom.reconstructionTypeAfcCheckbox.addEventListener('change', inputChangeCallback);

        // Monitor the state of the distance input
        this.dom.distanceInput.addEventListener('change', inputChangeCallback);
    }

    prefillFromQuizResponses(userResponses = {}) {
        // Set the values on the form
        prefillForm.call(
            this,
            userResponses.type,
            userResponses.query,
            userResponses.ibr,
            userResponses.afbr,
            userResponses.afc,
            '',
        );
    }

    validate(trimTextInputs = true) {
        // Reset our validated query parameters
        this.lastValidatedQueryParameters = null;

        // Attempt to assemble new query parameters as we validate the form
        const newQueryParameters = {};
        let enableSubmit = true;
        let enableClearFilters = false;
        const names = settings.queryParameterNames;

        // Specialist type must be set
        if (this.dom.specialistInput.value) {
            newQueryParameters[names.specialistType] =
                this.dom.specialistInput.value;
        } else {
            enableSubmit = false;
        }

        // Query must not be empty
        const queryValue = this.dom.queryInput.value.trim();
        if (queryValue) {
            newQueryParameters[names.query] = queryValue;

            if (trimTextInputs) {
                this.dom.queryInput.value = queryValue;
            }
        } else {
            enableSubmit = false;
        }

        if (
            this.dom.specialistInput.value === 'plastic-surgeon' ||
            this.dom.specialistInput.value === 'both'
        ) {
            // Reconstruction types are optional
            newQueryParameters[names.reconstructionTypeIbr] = this.dom.
                reconstructionTypeIbrCheckbox.checked.toString();
            newQueryParameters[names.reconstructionTypeAfbr] = this.dom.
                reconstructionTypeAfbrCheckbox.checked.toString();
            newQueryParameters[names.reconstructionTypeAfc] = this.dom.
                reconstructionTypeAfcCheckbox.checked.toString();

            if (
                !this.dom.reconstructionTypeIbrCheckbox.checked ||
                !this.dom.reconstructionTypeAfbrCheckbox.checked ||
                !this.dom.reconstructionTypeAfcCheckbox.checked
            ) {
                // Filters aren't in their default state
                enableClearFilters = true;
            }
        }

        // Distance is optional
        if (this.dom.distanceInput.value) {
            newQueryParameters[names.distance] = this.dom.
                distanceInput.value;

            // Filters aren't in their default state
            enableClearFilters = true;
        }

        if (enableClearFilters) {
            if (!this.clearFiltersEnabled) {
                // Enable clear filters button
                this.clearFiltersEnabled = true;
                this.dom.clearFiltersButton.disabled = false;
            }
        } else {
            if (this.clearFiltersEnabled) {
                // Disable clear filters button
                this.clearFiltersEnabled = false;
                this.dom.clearFiltersButton.disabled = true;
            }
        }

        if (!enableSubmit) {
            if (this.submitEnabled) {
                // Disable submit button
                this.submitEnabled = false;
                this.dom.submitButton.disabled = true;
            }

            // Stop here
            return false;
        }

        // Validation succeeded
        if (!this.submitEnabled) {
            // Enable submit button
            this.submitEnabled = true;
            this.dom.submitButton.disabled = false;
        }

        // Update our validated query parameters and return a success indicator
        this.lastValidatedQueryParameters = newQueryParameters;
        return true;
    }
}

/*
    Internal functions
*/

/**
 * @this Form
 * @param {*} specialistTypeValue
 * @param {*} queryValue
 * @param {*} reconstructionTypeIbrValue
 * @param {*} reconstructionTypeAfbrValue
 * @param {*} reconstructionTypeAfcValue
 * @param {*} distanceValue
 */
function prefillForm(
    specialistTypeValue,
    queryValue,
    reconstructionTypeIbrValue,
    reconstructionTypeAfbrValue,
    reconstructionTypeAfcValue,
    distanceValue,
) {
    for (let i = 0; i < this.dom.specialistInput.options.length; i++) {
        const option = this.dom.specialistInput.options[i];
        if (option.value == specialistTypeValue) {
            this.dom.specialistInput.selectedIndex = i;
            break;
        }
    }

    this.dom.queryInput.value = queryValue || '';

    this.dom.reconstructionTypeIbrCheckbox.checked =
        reconstructionTypeIbrValue;
    this.dom.reconstructionTypeAfbrCheckbox.checked =
        reconstructionTypeAfbrValue;
    this.dom.reconstructionTypeAfcCheckbox.checked =
        reconstructionTypeAfcValue;

    for (let i = 0; i < this.dom.distanceInput.options.length; i++) {
        const option = this.dom.distanceInput.options[i];
        if (option.value == distanceValue) {
            this.dom.distanceInput.selectedIndex = i;
            break;
        }
    }
}

/**
 * @this Form
 */
function prefillFormFromUrl() {
    const names = settings.queryParameterNames;

    // Check for query values
    const url = new URL(window.location.href);
    const queryParameters = {};
    for (const [key, value] of url.searchParams) {
        queryParameters[key] = value.trim();
    }

    const parseReconstructionType = (name) => {
        const value = queryParameters[name];
        if (value === 'false') {
            return false;
        } else {
            // Default
            return true;
        }
    };

    // Set the values on the form
    prefillForm.call(
        this,
        queryParameters[names.specialistType],
        queryParameters[names.query],
        parseReconstructionType(names.reconstructionTypeIbr),
        parseReconstructionType(names.reconstructionTypeAfbr),
        parseReconstructionType(names.reconstructionTypeAfc),
        queryParameters[names.distance],
    );
}
